import { alpha, styled } from '@mui/material';
import { useEffect, useState } from 'react';

import { usePageLayout } from '@@containers/App/PageLayout/PageLayoutContext';
import { type PageLayoutArea } from '@@containers/App/PageLayout/types';

import { useShortcut } from '../useShortcut';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

const REGION_ORDER: PageLayoutArea[] = ['nav', 'head', 'sidebar', 'main', 'toolbar', 'foot'];

const RegionHighlight = styled('div')(({ theme }) => ({
    position: 'fixed',
    border: `${theme.borders[1]} solid ${theme.palette.secondary.main}`,
    backgroundColor: alpha(theme.palette.secondary.main, theme.opacityFactors.low),
    borderRadius: theme.shape.borderRadius,
    pointerEvents: 'none',
    zIndex: theme.zIndex.layer4,
}));

const CycleRegionShortcut = () => {
    const refs = usePageLayout();
    const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
    const [highlightedRect, setHighlightedRect] = useState<DOMRect | null>(null);

    const cycleRegion = () => {
        const active = document.activeElement;
        const current = REGION_ORDER.findIndex((area) => refs[area].current?.contains(active));
        const { length } = REGION_ORDER;

        for (let step = 1; step <= length; step++) {
            const area = REGION_ORDER[(current + step) % length];
            const container = area ? refs[area].current : null;

            if (container?.querySelector(FOCUSABLE_SELECTOR)) {
                container.setAttribute('tabindex', '-1');
                container.style.outline = 'none';
                container.focus();
                container.scrollIntoView({ block: 'nearest' });
                setHighlightedElement(container);
                setHighlightedRect(container.getBoundingClientRect());
                container.addEventListener(
                    'blur',
                    () => {
                        container.removeAttribute('tabindex');
                        container.style.removeProperty('outline');
                        setHighlightedElement(null);
                        setHighlightedRect(null);
                    },
                    { once: true },
                );

                return;
            }
        }
    };

    useShortcut('cycleRegion', () => cycleRegion());

    useEffect(() => {
        if (!highlightedElement) {
            return;
        }

        const reposition = () => setHighlightedRect(highlightedElement.getBoundingClientRect());
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);

        return () => {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [highlightedElement]);

    if (!highlightedRect) {
        return null;
    }

    return (
        <RegionHighlight
            data-unity-region-highlight
            style={{
                top: highlightedRect.top,
                left: highlightedRect.left,
                width: highlightedRect.width,
                height: highlightedRect.height,
            }}
        />
    );
};

export default CycleRegionShortcut;
